
import { supabase } from '@/integrations/supabase/client';
import { BadgeType } from './types';
import { markBadgeAsNotified } from './sessionStorage';
import { useNotifications } from '@/hooks/useNotifications';

export const awardBadge = async (badgeType: BadgeType, userId: string, userRole: string | undefined): Promise<void> => {
  try {
    // Solo procesar badges para usuarios con rol 'socio'
    if (userRole !== 'socio') {
      console.log(`Usuario ${userId} con rol ${userRole} no acumula logros`);
      return;
    }

    // Verificar si el badge ya existe
    const { data: existingBadge, error: existingError } = await supabase
      .from('user_badges')
      .select('*, badges!inner(*)')
      .eq('user_id', userId)
      .eq('badges.badge_type', badgeType)
      .maybeSingle();

    if (existingError) {
      console.error('Error verificando badge existente:', existingError);
      return;
    }

    if (existingBadge) {
      console.log(`Badge ${badgeType} ya obtenido por el usuario ${userId}, no mostrando notificación`);
      return; // El usuario ya tiene este badge, no mostramos notificación
    }

    // Obtener el badge a otorgar
    const { data: badge, error: badgeError } = await supabase
      .from('badges')
      .select('*')
      .eq('badge_type', badgeType)
      .single();

    if (badgeError) {
      console.error('Error obteniendo datos del badge:', badgeError);
      return;
    }

    if (!badge) {
      console.error('No se encontró el badge:', badgeType);
      return;
    }

    // Otorgar el badge
    const { error: insertError } = await supabase
      .from('user_badges')
      .insert({
        user_id: userId,
        badge_id: badge.id,
      });
      
    if (insertError) {
      console.error('Error otorgando badge:', insertError);
      return;
    }

    // Marcar como notificado para esta sesión
    markBadgeAsNotified(userId, badgeType);

    // Mostrar notificación de logro con deduplicación
    const { success } = useNotifications();
    success("¡Nuevo Logro Desbloqueado!", {
      description: `Has obtenido: ${badge.name}`,
      duration: 6000
    });
    
    console.log(`Otorgado nuevo badge ${badgeType} al usuario ${userId}`);
  } catch (error) {
    console.error('Error awarding badge:', error);
  }
};

export const checkUserBadges = async (
  userId: string, 
  userRole: string | undefined,
  userSettings: any, 
  routineProgress: any[], 
  classSubscriptions: any[]
): Promise<void> => {
  // Solo procesar badges para usuarios con rol 'socio'
  if (userRole !== 'socio') {
    console.log(`Usuario con rol ${userRole} no acumula logros`);
    return;
  }
  
  // Tutorial completado
  if (userSettings?.has_seen_tutorial) {
    await awardBadge('tutorial_completed', userId, userRole);
  }

  // Primera rutina
  if (routineProgress && routineProgress.length > 0) {
    await awardBadge('first_routine', userId, userRole);
  }

  // Primera clase
  if (classSubscriptions && classSubscriptions.length > 0) {
    await awardBadge('first_class', userId, userRole);
  }

  // Constancia (3 días seguidos)
  if (routineProgress && routineProgress.length >= 3) {
    const last3Days = routineProgress.slice(0, 3).map(p => 
      new Date(p.completed_at).toDateString()
    );
    const uniqueDays = new Set(last3Days);
    if (uniqueDays.size === 3) {
      await awardBadge('workout_streak', userId, userRole);
    }
  }

  // Amante de las clases (5 clases diferentes)
  if (classSubscriptions && classSubscriptions.length >= 5) {
    const uniqueClasses = new Set(classSubscriptions.map(s => s.class_id));
    if (uniqueClasses.size >= 5) {
      await awardBadge('class_enthusiast', userId, userRole);
    }
  }
};
